import { Injectable } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { ApiServerService } from '../../core/api';
import { ElasticFirestoreBase } from '../../core/db/elastic-firestore-base';
import { defaultElasticConverter } from '../../core/db/elastic.helper';
import { ElasticQueryOptions, ElasticResult, ElasticSearchBody } from '../../core/db/elastic.models';
import { DEFAULT_DATE_PROPERTIES } from '../../core/db/firestore.helper';
import { Piggy, PiggyTransaction, PiggyTransactionOption, PiggyTransactionType } from './piggy.models';

export const piggyTransactionsElasticConverter = defaultElasticConverter<PiggyTransaction>(
  [...DEFAULT_DATE_PROPERTIES, 'payload.originalFeeDate']
);

/**
 * Piggy Firestore data-access service.
 * Extends {@link ElasticFirestoreBase} to reuse generic CRUD and query helpers.
 * Use inherited methods such as `get$`, `getOneWhere$`, `create`, `upsert`, and `update`
 * from higher layers (`piggy.service`) unless a piggy-specific query is required.
 */
@Injectable({ providedIn: 'root' })
export class PiggyDbService extends ElasticFirestoreBase<Piggy> {
  constructor(firestore: Firestore, apiServerService: ApiServerService) {
    super(
      'piggy',
      firestore,
      apiServerService,
    );
  }

  /**
   * Get paginated piggy transactions for a user, with ElasticSearch query
   * @param userId User ID to filter transactions
   * @param pagination Pagination parameters (current page and limit)
   * @returns Observable of part of PiggyTransaction with total count for pagination
   */
  getPiggyTransactionsPage$(
    userId: string,
    pagination: { current: number, limit: number; },
  ): Observable<ElasticResult<Partial<PiggyTransaction>>> {
    const body: ElasticSearchBody = {
      query: {
        bool: {
          filter: [
            { term: { 'user.id': userId } },
          ]
        },
      },
      _source: [
        'amount', 'ref', 'finalBalance', 'payload.feeType', 'payload.orderPriceNoTaxAmtBase', 'payload.limitedBase',
        'payload.feePct', 'payload.description', 'payload.product', 'payload.originalFeeDate._seconds',
        'createdAt._seconds', 'type', 'status',
      ],
      from: (pagination.current - 1) * pagination.limit,
      size: pagination.limit,
      sort: [{ 'createdAt._seconds': 'desc' }, { 'finalBalance': 'asc' }]
    };

    const options: ElasticQueryOptions<PiggyTransaction> = {
      index: 'piggytransactions',
      throttleDuration: 30_000,
      converter: piggyTransactionsElasticConverter,
    };
    return this.search$<Partial<PiggyTransaction>>(body, options);
  }

  /**
   * Get aggregated fee amounts for the current and previous month.
   * @param userId User ID to filter transactions
   * @param piggyTransactionType Transaction type filter (e.g. 'fee')
   * @param piggyTransactionOption Fee type filter (e.g. 'coupon')
   * @returns Promise with currentMonthAmount and previousMonthAmount
   */
  async getMonthlyFeeAmounts(
    userId: string,
    piggyTransactionType: PiggyTransactionType,
    piggyTransactionOption: PiggyTransactionOption
  ): Promise<{ currentMonthAmount: number; previousMonthAmount: number; }> {
    const serverTimeZone = 'Europe/Madrid';
    const body = {
      size: 0,
      query: {
        bool: {
          filter: [
            { term: { 'user.id': userId } },
            { term: { type: piggyTransactionType } },
            { term: { 'payload.feeType': piggyTransactionOption } },
          ]
        }
      },
      aggs: {
        monthly_grouping: {
          filters: {
            filters: {
              current_month: {
                range: {
                  'createdAt._seconds': {
                    gte: 'now/M',
                    lte: 'now',
                    time_zone: serverTimeZone
                  }
                }
              },
              previous_month: {
                range: {
                  'createdAt._seconds': {
                    gte: 'now-1M/M',
                    lt: 'now/M',
                    time_zone: serverTimeZone
                  }
                }
              }
            }
          },
          aggs: {
            total_amount: {
              sum: {
                field: 'amount'
              }
            }
          }
        }
      }
    };

    const options: ElasticQueryOptions<PiggyTransaction> = {
      index: 'piggytransactions',
      converter: piggyTransactionsElasticConverter,
    };

    const result = await this.search<PiggyTransaction>(body as any, options);
    const buckets = (result.aggregations as any)?.monthly_grouping?.buckets;

    return {
      currentMonthAmount: buckets?.current_month?.total_amount?.value || 0,
      previousMonthAmount: buckets?.previous_month?.total_amount?.value || 0,
    };
  }
}
